import { Link } from "react-router-dom";
import { ArrowRight, Headphones, RotateCcw, Truck } from "lucide-react";
import { fmtDate, money } from "@/lib/api";
import { OrderCard } from "@/components/account/OrderCard";
import { EmptyOrders } from "@/components/account/EmptyOrders";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-neutral-200 bg-white p-5">
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{label}</p>
    <p className="mt-2 font-heading text-xl font-bold" data-testid={testId}>
      {value}
    </p>
  </div>
);

const QUICK = [
  { to: "/policies/shipping", icon: Truck, label: "Shipping info" },
  { to: "/policies/refund", icon: RotateCcw, label: "Returns & refunds" },
  { to: "/contact", icon: Headphones, label: "Contact support" },
];

export const AccountOverview = ({ customer, onViewOrders }) => {
  const orders = customer.orders || [];
  const active = orders.filter((o) => !["FULFILLED", "CANCELLED"].includes(o.fulfillment_status?.code)).length;
  const last = orders[0];

  return (
    <div className="space-y-8" data-testid="account-overview">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">My Account</p>
        <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl" data-testid="account-greeting">
          Hi, {customer.first_name || "there"}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">Here's a quick look at your account.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Total orders" value={orders.length} testId="stat-total-orders" />
        <Stat label="In progress" value={active} testId="stat-active-orders" />
        <Stat
          label="Last order"
          value={last ? `${money(last.total, last.currency)} · ${fmtDate(last.date)}` : "—"}
          testId="stat-last-order"
        />
      </div>

      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Recent orders</h2>
          {orders.length > 2 && (
            <button
              type="button"
              onClick={onViewOrders}
              data-testid="overview-view-all-orders"
              className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 underline-offset-4 hover:underline"
            >
              View all <ArrowRight size={13} />
            </button>
          )}
        </div>
        <div className="mt-4 space-y-4" data-testid="account-orders">
          {orders.length ? orders.slice(0, 2).map((o) => <OrderCard key={o.id} order={o} />) : <EmptyOrders />}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-lg font-bold">Need a hand?</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {QUICK.map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4 text-sm font-semibold text-neutral-700 transition-colors hover:border-neutral-900"
            >
              <span className="grid h-9 w-9 place-items-center rounded-full bg-neutral-100 transition-colors group-hover:bg-neutral-900 group-hover:text-white">
                <Icon size={15} />
              </span>
              {label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};
